import { motion } from "framer-motion";

import { useMemo, useEffect, useState } from "react";

import useFinanceExtrato from "@/hooks/useFinanceExtrato";
import { authFetch } from "@/lib/authFetch";
import ExtratoHeader from "./extrato/ExtratoHeader";
import ExtratoToolbar from "./extrato/ExtratoToolbar";
import MultiSelectFilter from "./extrato/MultiSelectFilter";
import PeriodFilterDropdown from "./extrato/PeriodFilterDropdown";
import TransactionTable from "./extrato/TransactionTable";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PAYMENT_METHOD_OPTIONS = [
  { value: "Pix",            label: "Pix",            icon: "⚡" },
  { value: "Crédito",        label: "Crédito",        icon: "💳" },
  { value: "Débito",         label: "Débito",         icon: "💳" },
  { value: "Boleto",         label: "Boleto",         icon: "🧾" },
  { value: "TED",            label: "TED",            icon: "🏦" },
  { value: "DOC",            label: "DOC",            icon: "🏦" },
  { value: "Transferência",  label: "Transferência",  icon: "🔄" },
  { value: "Dinheiro",       label: "Dinheiro",       icon: "💵" },
  { value: "Other",          label: "Outros",         icon: "📦" },
];

// FIN-2 — ExtratoTab orquestrador.
// Hierarquia: Header (KPIs) → Toolbar (search/toggle/export) → Filtros
// (T9) → Tabela (T10).

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function ExtratoTab({ onTalk }) {
  const extrato = useFinanceExtrato();

  // Carrega listas auxiliares (accounts + categories used).
  const [accounts, setAccounts] = useState([]);
  const [categoriesUsed, setCategoriesUsed] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rA, rC] = await Promise.all([
          authFetch(`${API_URL}/api/accounts`),
          authFetch(`${API_URL}/api/finance/categories/used`),
        ]);
        if (rA.ok && !cancelled) {
          const data = await rA.json();
          setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
        }
        if (rC.ok && !cancelled) {
          const data = await rC.json();
          setCategoriesUsed(Array.isArray(data?.categories) ? data.categories : []);
        }
      } catch (_) { /* ignora — dropdowns ficam vazios */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const accountOptions = useMemo(() => (
    (accounts || []).filter(a => !a.deleted_at).map(a => ({
      value: a.id,
      label: a.name,
      tone: a.type,
    }))
  ), [accounts]);

  const categoryOptions = useMemo(() => (
    (categoriesUsed || []).map(c => ({
      value: c.category,
      label: c.category,
      icon: c.icon,
      count: c.count,
    }))
  ), [categoriesUsed]);

  // Em filtros de categoria, só "1" pode ser selecionado por enquanto
  // (backend ainda recebe string singular). UI continua multi-select pra
  // futuro — adapta passando array → single.
  const categorySelected = extrato.appliedFilters.category
    ? [extrato.appliedFilters.category]
    : [];
  const onCategoryChange = (arr) => {
    extrato.setFilter("category", arr.length > 0 ? arr[arr.length - 1] : "");
  };

  // T12 — pre-prompt contextual pra "Falar com Nexo sobre esta transação"
  const handleTalkAbout = useMemo(() => {
    if (!onTalk) return null;
    return (tx) => {
      const amountStr = Number(tx.amount || 0).toLocaleString("pt-BR", {
        style: "currency", currency: "BRL",
      });
      const typeLabel = tx.type === "income" ? "entrada" : "saída";
      const desc = (tx.description || tx.title || "transação").slice(0, 80);
      const cat = tx.category || "outros";
      const prompt = `O que você sabe sobre esta transação de ${amountStr} ` +
        `(${typeLabel}) para "${desc}", na categoria ${cat}?`;
      onTalk(prompt);
    };
  }, [onTalk]);

  const hasActiveFilters = useMemo(() => {
    const f = extrato.appliedFilters;
    return Boolean(
      (f.q && f.q.length > 0) ||
      (f.accountIds && f.accountIds.length > 0) ||
      (f.category && f.category.length > 0) ||
      (f.paymentMethods && f.paymentMethods.length > 0) ||
      (f.type && f.type !== "all") ||
      (f.startDate && f.endDate) ||
      (f.period && f.period !== "current_month")
    );
  }, [extrato.appliedFilters]);

  const filtersRow = (
    <>
      <MultiSelectFilter
        label="Contas"
        placeholder="Buscar conta..."
        options={accountOptions}
        selected={extrato.appliedFilters.accountIds || []}
        onChange={(arr) => extrato.setFilter("accountIds", arr)}
        searchable={accountOptions.length > 6}
        testId="extrato-filter-accounts"
      />
      <MultiSelectFilter
        label="Categoria"
        placeholder="Buscar categoria..."
        options={categoryOptions}
        selected={categorySelected}
        onChange={onCategoryChange}
        searchable={true}
        testId="extrato-filter-category"
      />
      <MultiSelectFilter
        label="Pagamento"
        placeholder="Filtrar pagamento..."
        options={PAYMENT_METHOD_OPTIONS}
        selected={extrato.appliedFilters.paymentMethods || []}
        onChange={(arr) => extrato.setFilter("paymentMethods", arr)}
        searchable={false}
        testId="extrato-filter-payment"
      />
      <PeriodFilterDropdown
        filters={extrato.appliedFilters}
        setManyFilters={extrato.setManyFilters}
      />
    </>
  );

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-extrato">
      {/* Header com 2 KPIs heroicos */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ROW_TRANSITION}
      >
        <ExtratoHeader
          summary={extrato.summary}
          totalCount={extrato.totalCount}
          isLoading={extrato.isLoading}
        />
      </motion.section>

      {/* Toolbar (search + toggle + export) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.1 }}
      >
        <ExtratoToolbar
          filters={extrato.filters}
          setFilter={extrato.setFilter}
          exportCsv={extrato.exportCsv}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={extrato.resetFilters}
          filtersRow={filtersRow}
        />
      </motion.section>

      {/* Tabela + infinite scroll */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.16 }}
      >
        <TransactionTable
          transactions={extrato.transactions}
          isLoading={extrato.isLoading}
          isAppending={extrato.isAppending}
          hasMore={extrato.hasMore}
          totalCount={extrato.totalCount}
          fetchMore={extrato.fetchMore}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={extrato.resetFilters}
          categoriesUsed={categoriesUsed}
          onPatch={extrato.patchTransaction}
          onTalkAbout={handleTalkAbout}
        />
      </motion.section>

      {extrato.error && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="extrato-error"
        >
          {extrato.error}
        </div>
      )}
    </div>
  );
}
